import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import type { CompteBoutique, RoleWithPermissions } from '@iobiz/shared'
import { creerCompte, majCompte } from './api.js'

/**
 * Créer ou modifier un compte de l'ÉQUIPE.
 *
 * ⭐ Ce compte vit dans le schéma de la boutique : il n'existe que là, et le
 * gérant en dispose entièrement. C'est ce qui rend cet écran possible sans le
 * moindre garde-fou sur les autres enseignes — il n'y a rien à protéger, parce
 * qu'il n'y a rien de partagé.
 *
 * ⚠️ Le mot de passe est FACULTATIF à la création. Sans lui, le compte reste
 * « invité » et ne peut pas entrer : c'est visible dans la liste, et c'est
 * honnête — un compte qu'on croit actif alors qu'il ne peut pas se connecter
 * fait perdre une matinée au comptoir.
 */
export function CompteBoutiqueDialog({
  storeId,
  compte,
  roles,
  onClose,
  onEnregistre,
}: {
  storeId: string
  /** Absent : création. Présent : modification. */
  compte?: CompteBoutique | undefined
  roles: RoleWithPermissions[]
  onClose: () => void
  onEnregistre: () => void
}): JSX.Element {
  const edition = Boolean(compte)
  const [prenom, setPrenom] = useState(compte?.firstName ?? '')
  const [nom, setNom] = useState(compte?.lastName ?? '')
  const [email, setEmail] = useState(compte?.email ?? '')
  const [telephone, setTelephone] = useState(compte?.phone ?? '')
  const [roleId, setRoleId] = useState(compte?.roleId ?? (roles[0]?.id ?? ''))
  const [motDePasse, setMotDePasse] = useState('')
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  async function enregistrer(): Promise<void> {
    setOccupe(true)
    setErreur(null)
    try {
      const base = {
        email: email.trim(),
        firstName: prenom.trim(),
        lastName: nom.trim(),
        phone: telephone.trim(),
        roleId,
      }
      if (compte) {
        // ⚠️ Le mot de passe n'est envoyé QUE s'il a été saisi : une chaîne
        // vide écraserait celui de la personne, qui ne pourrait plus entrer
        // sans que rien ne l'explique.
        await majCompte(storeId, compte.id, {
          ...base,
          ...(motDePasse ? { password: motDePasse } : {}),
        })
      } else {
        await creerCompte(storeId, { ...base, ...(motDePasse ? { password: motDePasse } : {}) })
      }
      onEnregistre()
      onClose()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
    } finally {
      setOccupe(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{edition ? 'Modifier le compte' : 'Nouveau compte'}</DialogTitle>
      <DialogContent>
        {erreur && (
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            {erreur}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 3, mt: 1 }}>
          <TextField label="Prénom" value={prenom} onChange={(e) => setPrenom(e.target.value)} />
          <TextField label="Nom" value={nom} onChange={(e) => setNom(e.target.value)} />
          <TextField
            label="Adresse e-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText="Identifiant de connexion, propre à cette boutique."
          />
          <TextField
            label="Téléphone"
            value={telephone}
            onChange={(e) => setTelephone(e.target.value)}
          />
          <TextField
            select
            label="Rôle"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            {roles.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={edition ? 'Nouveau mot de passe' : 'Mot de passe'}
            type="password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            helperText={
              edition
                ? 'Laisser vide pour ne pas le changer.'
                : 'Facultatif — sans mot de passe, le compte ne peut pas encore se connecter.'
            }
          />

          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            La personne se connectera sur l’adresse de la boutique, pas sur l’accueil général.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          disabled={occupe || email.trim() === '' || roleId === ''}
          onClick={() => void enregistrer()}
        >
          {edition ? 'Enregistrer' : 'Créer le compte'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
